import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import ScrollIndicator from '../components/ScrollIndicator';

const FAQ = () => {
  const [openSections, setOpenSections] = useState({
    visrupt: false,
    vision: false,
    visflow: false
  });

  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setShowScrollTop(scrollTop > 800);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="h-screen overflow-y-scroll snap-y snap-mandatory scroll-container">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white min-h-[90vh] snap-start flex flex-col justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-slate-900/0"></div>
        <div className="container-width relative flex-1 flex flex-col justify-center items-center py-16 sm:py-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">Frequently Asked Questions</h1>
            <div className="space-y-4">
              <p className="text-base sm:text-lg text-slate-300">
                Find detailed answers about our services and solutions. We're here to help you understand how we can best serve your needs.
              </p>
            </div>
          </div>
        </div>
        <ScrollIndicator text="Explore FAQs" />
      </section>

      {/* FAQ Sections */}
      <section className="min-h-screen snap-start bg-slate-50 flex items-center py-24">
        <div className="container-width">
          {/* ViSRUPT FAQs */}
          <div id="visrupt-section" className="mb-12">
            <button
              onClick={() => toggleSection('visrupt')}
              className="w-full flex justify-between items-center bg-white p-6 rounded-lg hover:bg-slate-100 transition shadow-md"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-center w-full pr-8">ViSRUPT – Custom Software & Automation Solutions FAQs</h2>
              {openSections.visrupt ? <ChevronUp className="w-6 h-6 flex-shrink-0" /> : <ChevronDown className="w-6 h-6 flex-shrink-0" />}
            </button>
            <div className={`mt-6 space-y-6 bg-white rounded-lg p-6 shadow-md transition-all duration-300 ${openSections.visrupt ? 'opacity-100 max-h-[2000px]' : 'opacity-0 max-h-0 overflow-hidden'}`}>
              <FAQItem
                question="What is ViSRUPT?"
                answer="ViSRUPT is a custom software development firm focused on building trading systems, Python automation tools, chatbots for Telegram & Discord, game bots, and web solutions. Our goal is to create highly efficient, tailored solutions that enhance productivity and optimize workflows."
              />
              <FAQItem
                question="What services does ViSRUPT offer?"
                answer={<>
                  ViSRUPT specializes in:
                  <ul className="list-disc ml-6 mt-2">
                    <li>Algorithmic trading solutions (through our ViSiON division)</li>
                    <li>Python-based automation tools to streamline business processes</li>
                    <li>Telegram & Discord chatbots for customer engagement and automation</li>
                    <li>Game bots for Discord to enhance gaming experiences</li>
                    <li>Web development for modern, functional websites and platforms</li>
                  </ul>
                </>}
              />
              <FAQItem
                question="What makes ViSRUPT different?"
                answer="We focus on custom-built solutions, ensuring that every product we develop is tailored to the specific needs of our clients. We do not offer generic, one-size-fits-all software—every solution is designed with precision, efficiency, and reliability in mind."
              />
              <FAQItem
                question="How does ViSRUPT ensure confidentiality and security?"
                answer="We take confidentiality seriously. For custom projects, we sign an NDA (Non-Disclosure Agreement) and provide a formal acknowledgment stating that the software/code belongs to you. Your data, strategies, and intellectual property remain protected at all times."
              />
              <FAQItem
                question="Do I need technical expertise to use ViSRUPT's solutions?"
                answer="Not at all. We handle everything from development to deployment, ensuring a user-friendly experience. Our solutions integrate seamlessly into your existing workflows, so you don't need to worry about the technical details."
              />
              <FAQItem
                question="Does ViSRUPT provide ongoing support?"
                answer="Yes. We offer dedicated support to ensure smooth implementation and operation of all our solutions. Whether it's troubleshooting, updates, or modifications, we are committed to providing assistance whenever you need it."
              />
              <FAQItem
                question="How can ViSRUPT help businesses with automation?"
                answer="Automation reduces manual workload, increases efficiency, and minimizes errors. We provide custom automation tools to handle repetitive tasks, allowing businesses to focus on more strategic activities."
              />
              <FAQItem
                question="How can I get started with ViSRUPT?"
                answer="Getting started is simple. Contact us with your requirements, and we'll guide you through the process. You can reach us at contact@visrupt.in or call +91-7522979568."
              />
            </div>
          </div>

          {/* ViSiON FAQs */}
          <div id="vision-section" className="mb-12">
            <button
              onClick={() => toggleSection('vision')}
              className="w-full flex justify-between items-center bg-white p-6 rounded-lg hover:bg-slate-100 transition shadow-md"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-center w-full pr-8">ViSiON – Algorithmic Trading & Backtesting Solutions FAQs</h2>
              {openSections.vision ? <ChevronUp className="w-6 h-6 flex-shrink-0" /> : <ChevronDown className="w-6 h-6 flex-shrink-0" />}
            </button>
            <div className={`mt-6 space-y-6 bg-white rounded-lg p-6 shadow-md transition-all duration-300 ${openSections.vision ? 'opacity-100 max-h-[2000px]' : 'opacity-0 max-h-0 overflow-hidden'}`}>
              <FAQItem
                question="What is ViSiON?"
                answer="ViSiON is a specialized division of ViSRUPT focused on custom algorithmic trading solutions and backtesting services. We develop high-performance trading algorithms that help traders execute trades efficiently, manage risks, and optimize market opportunities with precision and speed."
              />
              <FAQItem
                question="What services does ViSiON offer?"
                answer={<>
                  ViSiON provides:
                  <ul className="list-disc ml-6 mt-2">
                    <li>Custom algorithmic trading solutions tailored to individual trading strategies</li>
                    <li>Backtesting services to analyze and optimize strategies before live deployment</li>
                    <li>Seamless integration of trading algorithms with broker APIs</li>
                    <li>Automated risk management solutions to enhance trading efficiency</li>
                  </ul>
                </>}
              />
              <FAQItem
                question="Do I need coding skills to use algo trading with ViSiON?"
                answer="No. We handle all the coding and deployment for you. You simply share your trading strategy, and we develop a fully automated solution that integrates with your trading account."
              />
              <FAQItem
                question="How does ViSiON ensure my trading strategy remains confidential?"
                answer="Confidentiality is a top priority. We sign a Non-Disclosure Agreement (NDA) and provide a formal acknowledgment that your trading strategy belongs to you. You receive full ownership of the code, ensuring complete security and control."
              />
              <FAQItem
                question="How can I test my trading strategy before using it live?"
                answer="ViSiON provides backtesting services, allowing you to test your strategy against historical market data. This helps refine and optimize strategies before executing real trades."
              />
              <FAQItem
                question="What markets can I trade using ViSiON's algo solutions?"
                answer="Our algorithms can be designed for stocks, futures, options, forex, and crypto markets, depending on your trading requirements."
              />
              <FAQItem
                question="What is the cost of developing an algo trading system with ViSiON?"
                answer="Pricing depends on the complexity of your trading strategy and required features. Our services start at ₹3999, ensuring accessibility for both retail traders and financial institutions."
              />
              <FAQItem
                question="How do I get started with ViSiON?"
                answer="Simply share your trading strategy with us, and we'll handle the development and deployment. You can contact us at visionforalgo@visrupt.in or call +91-7522979568 for more details."
              />
            </div>
          </div>

          {/* ViSFLOW FAQs */}
          <div id="visflow-section">
            <button
              onClick={() => toggleSection('visflow')}
              className="w-full flex justify-between items-center bg-white p-6 rounded-lg hover:bg-slate-100 transition shadow-md"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-center w-full pr-8">ViSFLOW – Process Automation & Chatbot Development FAQs</h2>
              {openSections.visflow ? <ChevronUp className="w-6 h-6 flex-shrink-0" /> : <ChevronDown className="w-6 h-6 flex-shrink-0" />}
            </button>
            <div className={`mt-6 space-y-6 bg-white rounded-lg p-6 shadow-md transition-all duration-300 ${openSections.visflow ? 'opacity-100 max-h-[2000px]' : 'opacity-0 max-h-0 overflow-hidden'}`}>
              <FAQItem
                question="What is ViSFLOW?"
                answer="ViSFLOW is our dedicated division for Python automation, chatbot development, and process optimization. We help businesses streamline operations by automating repetitive tasks, building intelligent chatbots, and creating game bots for enhanced user engagement."
              />
              <FAQItem
                question="What types of automation solutions does ViSFLOW provide?"
                answer={<>
                  ViSFLOW specializes in:
                  <ul className="list-disc ml-6 mt-2">
                    <li>Custom automation tools – Automating repetitive tasks to save time and reduce manual errors</li>
                    <li>Business process automation – Streamlining workflows for efficiency and accuracy</li>
                    <li>Data extraction and processing – Automating data handling for better decision-making</li>
                  </ul>
                </>}
              />
              <FAQItem
                question="What kind of chatbots does ViSFLOW develop?"
                answer={<>
                  We build Telegram and Discord chatbots tailored for:
                  <ul className="list-disc ml-6 mt-2">
                    <li>Customer support – Automating responses and handling inquiries</li>
                    <li>Community management – Moderating chats, managing users, and engaging audiences</li>
                    <li>Trading assistance – Automating trade alerts and execution based on custom strategies</li>
                  </ul>
                </>}
              />
              <FAQItem
                question="Does ViSFLOW develop game bots for Discord?"
                answer="Yes! ViSFLOW develops custom game bots for Discord that can enhance gameplay, automate in-game functionalities, and create interactive experiences for gaming communities."
              />
              <FAQItem
                question="How can automation help my business?"
                answer={<>
                  Automation can:
                  <ul className="list-disc ml-6 mt-2">
                    <li>Reduce workload by handling repetitive tasks</li>
                    <li>Increase accuracy by minimizing human errors</li>
                    <li>Save time and costs by optimizing processes</li>
                    <li>Improve customer engagement through chatbots that provide instant responses</li>
                  </ul>
                </>}
              />
              <FAQItem
                question="Do I need technical knowledge to use ViSFLOW's automation solutions?"
                answer="Not at all. We handle the development, deployment, and integration of automation tools and chatbots, ensuring they work seamlessly with your existing systems."
              />
              <FAQItem
                question="How secure are the automation tools and chatbots developed by ViSFLOW?"
                answer="We prioritize confidentiality and ensure that all custom-built solutions are secure. If required, we sign an NDA and formal acknowledgment to guarantee that your automation tools remain exclusive to you."
              />
              <FAQItem
                question="How can I get started with ViSFLOW?"
                answer="Simply reach out to us with your requirements, and we'll guide you through the process. You can contact us at visflow@visrupt.in or call +91-7522979568."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="snap-start">
        <div className="bg-white py-24">
          <div className="container-width">
            <div className="bg-blue-500 text-white rounded-xl p-8 sm:p-10 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">Still have questions?</h2>
              <p className="text-lg mb-8">Our team is ready to help you find the perfect solution for your needs.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <div className="space-y-4">
                  <p className="text-lg">
                    Email us at:{' '}
                    <a href="mailto:contact@visrupt.in" className="underline hover:text-blue-100">
                      contact@visrupt.in
                    </a>
                  </p>
                  <p className="text-lg">
                    Call us:{' '}
                    <a href="tel:+917522979568" className="underline hover:text-blue-100">
                      +91-7522979568
                    </a>
                  </p>
                </div>
                <div className="sm:ml-8">
                  <a
                    href="https://calendly.com/visrupt/schedule-a-call"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-white text-blue-500 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 group"
                  >
                    <Calendar className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">Schedule a Call</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 bg-blue-500 text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
        }`}
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-6 h-6" />
      </button>
    </div>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-slate-200 pb-4">
      <button
        className="w-full text-left flex justify-between items-center py-4"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-lg font-semibold pr-8">{question}</h3>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-500 flex-shrink-0" />
        )}
      </button>
      <div className={`mt-2 text-slate-600 prose prose-slate max-w-none transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'}`}>
        {answer}
      </div>
    </div>
  );
};

export default FAQ;